import React, { useState, useEffect, useRef } from 'react';
import api, { API_BASE_URL } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

export default function AgentProfile() {
    const [profile, setProfile] = useState({
        numeComplet: '', telefon: '', email: '', avatarUrl: '',
        ambassadorTarget: 3, ambassadorReward: ''
    });
    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const [passwords, setPasswords] = useState({
        parolaNoua: '', confirmaParola: ''
    });

    useEffect(() => { fetchProfile(); }, []);

    const fetchProfile = async () => {
        try {
            const res = await api.get('/agent/profile');
            if (res.data.success) setProfile(res.data.agent);
        } catch (err) {
            setError('Eroare la încărcarea profilului.');
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        setUploading(true);
        setMessage('');
        try {
            const res = await api.post('/agent/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data.success) {
                setProfile(prev => ({ ...prev, avatarUrl: res.data.url }));
                setMessage('Imagine încărcată! Apasă Salvează pentru a o păstra.');
            }
        } catch (err) {
            setError('Eroare la încărcarea imaginii.');
        } finally {
            setUploading(false);
        }
        e.target.value = '';
    };

    const handleFullSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        setError('');

        if (passwords.parolaNoua) {
            if (passwords.parolaNoua !== passwords.confirmaParola) {
                setError('Parolele noi nu coincid.');
                setSaving(false);
                return;
            }
            if (passwords.parolaNoua.length < 8) {
                setError('Parola nouă trebuie să aibă minim 8 caractere.');
                setSaving(false);
                return;
            }
        }

        const payload = {
            numeComplet: profile.numeComplet,
            telefon: profile.telefon,
            avatarUrl: profile.avatarUrl,
            ambassadorTarget: profile.ambassadorTarget != null ? Number(profile.ambassadorTarget) : undefined,
            ambassadorReward: profile.ambassadorReward != null ? profile.ambassadorReward : undefined,
            newPassword: passwords.parolaNoua || undefined
        };

        try {
            const res = await api.put('/agent/profile', payload);
            if (res.data.success) {
                setMessage('Profil salvat cu succes!');
                setProfile(prev => ({ ...prev, ...res.data.user }));
                setPasswords({ parolaNoua: '', confirmaParola: '' });
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Eroare la actualizare.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-t-2 border-brand-blue animate-spin"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-6 relative">

            {message && (
                <div className="mb-6 p-4 rounded-xl border flex items-center gap-3 bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                    <div className="bg-emerald-500/20 p-2 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>
                    <p className="font-medium">{message}</p>
                </div>
            )}
            {error && (
                <div className="mb-6 p-4 rounded-xl border flex items-center gap-3 bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400">
                    <div className="bg-rose-500/20 p-2 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></div>
                    <p className="font-medium">{error}</p>
                </div>
            )}

            <form onSubmit={handleFullSubmit} className="space-y-6">

                {/* Header Card / Avatar */}
                <div className="relative overflow-hidden rounded-[2rem] bg-indigo-600 border border-gray-100 dark:border-white/5 shadow-xl">
                    <div className="px-8 py-10 flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
                        <div className="relative flex-shrink-0">
                            <div
                                className="w-[100px] h-[100px] rounded-3xl border-2 border-indigo-400 bg-gray-900 overflow-hidden cursor-pointer flex items-center justify-center text-4xl font-bold text-indigo-400"
                                onClick={handleAvatarClick}
                            >
                                {profile.avatarUrl ? (
                                    <img src={`${API_BASE_URL}${profile.avatarUrl}`} alt="Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <span>{profile.numeComplet ? profile.numeComplet.charAt(0).toUpperCase() : 'A'}</span>
                                )}
                            </div>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                        </div>

                        <div className="flex-1 text-white">
                            <h1 className="text-3xl font-extrabold tracking-tight">{profile.numeComplet || 'Agent'}</h1>
                            <p className="text-indigo-200 font-medium mt-1 flex items-center justify-center md:justify-start gap-2">
                                <span className="w-2 h-2 rounded-full bg-green-400"></span> Rol: Agent Vânzări
                            </p>
                        </div>

                        <div>
                            <button type="submit" disabled={saving} className="bg-white text-indigo-700 hover:bg-gray-100 px-6 py-2.5 rounded-xl font-bold shadow-lg transition-all disabled:opacity-50 flex items-center gap-2">
                                {saving ? 'Se salvează...' : <><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg> Salvează Modificări</>}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Secțiunea 1: Informații Personale */}
                <div className="bg-white dark:bg-[#161a28] rounded-[2rem] p-8 shadow-sm border border-gray-100 dark:border-white/5">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                        <span className="text-brand-blue">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                        </span>
                        Informații Personale
                    </h3>

                    <div className="space-y-5">
                        <div>
                            <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">NUME COMPLET / COMPANIE</label>
                            <input
                                type="text" value={profile.numeComplet} onChange={(e) => setProfile({ ...profile, numeComplet: e.target.value })}
                                className="w-full bg-gray-50 dark:bg-[#111420] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-gray-100 outline-none"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">EMAIL (LOGIN)</label>
                                <input
                                    type="email" value={profile.email || ''} disabled
                                    className="w-full bg-gray-100 dark:bg-[#111420] opacity-60 border-0 rounded-xl px-4 py-3 text-sm text-gray-500 cursor-not-allowed"
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">TELEFON</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect><path d="M12 18h.01"></path></svg>
                                    </div>
                                    <input
                                        type="tel" value={profile.telefon || ''} onChange={(e) => setProfile({ ...profile, telefon: e.target.value })}
                                        className="w-full bg-gray-50 dark:bg-[#111420] border-0 rounded-xl py-3 pl-10 pr-4 text-sm text-gray-900 dark:text-gray-100 outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Program Ambasadori */}
                <div className="bg-white dark:bg-[#161a28] rounded-[2rem] p-8 shadow-sm border border-gray-100 dark:border-white/5">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                        <span className="text-amber-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                        </span>
                        Program Ambasadori
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Clienții care completează formularul pot deveni ambasadori și primi un link unic. Setează target-ul și recompensa afișată pe pagina de mulțumire.</p>
                    <div className="space-y-5">
                        <div>
                            <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">TARGET CLIENȚI (număr de recomandări)</label>
                            <input
                                type="number"
                                min={1}
                                value={profile.ambassadorTarget ?? 3}
                                onChange={(e) => setProfile({ ...profile, ambassadorTarget: e.target.value === '' ? 3 : parseInt(e.target.value, 10) || 3 })}
                                className="w-full bg-gray-50 dark:bg-[#111420] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-gray-100 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">RECOMPENSĂ (ex: Voucher 50 RON)</label>
                            <input
                                type="text"
                                value={profile.ambassadorReward ?? ''}
                                onChange={(e) => setProfile({ ...profile, ambassadorReward: e.target.value })}
                                placeholder="ex: Voucher 50 RON"
                                className="w-full bg-gray-50 dark:bg-[#111420] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-gray-100 outline-none"
                            />
                        </div>
                    </div>
                </div>

                {/* Secțiunea 2: Securitate */}
                <div className="bg-white dark:bg-[#161a28] rounded-[2rem] p-8 shadow-sm border border-gray-100 dark:border-white/5">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                        <span className="text-purple-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        </span>
                        Securitate
                    </h3>

                    <div className="space-y-5">
                        <div>
                            <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">PAROLĂ NOUĂ</label>
                            <input
                                type="password" value={passwords.parolaNoua} onChange={(e) => setPasswords({ ...passwords, parolaNoua: e.target.value })} placeholder="••••••••"
                                className="w-full bg-gray-50 dark:bg-[#111420] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-gray-100 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold tracking-wider text-gray-500 uppercase mb-2">CONFIRMĂ PAROLA</label>
                            <input
                                type="password" value={passwords.confirmaParola} onChange={(e) => setPasswords({ ...passwords, confirmaParola: e.target.value })} placeholder="••••••••"
                                className="w-full bg-gray-50 dark:bg-[#111420] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-gray-100 outline-none"
                            />
                        </div>
                        <p className="text-xs text-gray-400">Lasă gol dacă nu dorești schimbarea parolei actuale.</p>
                    </div>
                </div>

                {/* Secțiunea 3: Interfață */}
                <div className="bg-white dark:bg-[#161a28] rounded-[2rem] p-8 shadow-sm border border-gray-100 dark:border-white/5">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                        <span className="text-yellow-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                        </span>
                        Interfață
                    </h3>

                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-bold text-gray-900 dark:text-white">Mod Întunecat</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Confort vizual sporit noaptea</p>
                        </div>
                        <button
                            type="button"
                            onClick={toggleTheme}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ${theme === 'dark' ? 'bg-brand-blue' : 'bg-gray-300'}`}
                        >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
